/**
 * Client fetch tipizzato per l'API tornei.
 *
 * Dev: Vite proxy /api -> http://127.0.0.1:8002
 * Prod: stesso origin (Caddy route)
 */

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export type HealthCheck = {
  status: "ok" | "degraded";
  version: string;
  started_at: string;
  uptime_seconds: number;
  checks: Record<string, boolean>;
};

export type Pagination = {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
};

export type Page<T> = {
  items: T[];
  pagination: Pagination;
};

// Tournament
export type TournamentStatus =
  | "DRAFT"
  | "TEXTS_GENERATED"
  | "TEXTS_APPROVED"
  | "FLYER_GENERATED"
  | "FLYER_APPROVED"
  | "EMAIL_COMPOSED"
  | "EMAILS_SENT"
  | "LANDING_DEPLOYED"
  | "WHATSAPP_SENT"
  | "SOCIAL_RUNNING"
  | "COMPLETED"
  | "FAILED";

export type TournamentSummary = {
  slug: string;
  name: string;
  date: string; // YYYY-MM-DD
  status: TournamentStatus;
  current_step: number | null;
  next_action: string | null;
  updated_at: string | null;
};

export type TournamentCategory = {
  name: string;
  level: string;
  max_teams: number;
  price_community: number;
  price_non_community: number;
  playtomic_link: string;
  time_start: string; // "15:00" - se vuoto usa tournament.time_start
  time_end: string;
  bitly_link: string; // generato da Bitly
};

export type TournamentInput = {
  name: string;
  slug: string;
  date: string;
  time_start?: string; // deprecato, ora per categoria
  time_end?: string;
  location: string;
  categories: TournamentCategory[];
  formula: string;
  match_duration_min: number;
  guaranteed_matches: number;
  prizes: Record<string, string>;
  registration_deadline?: string | null;
  /** Partner principale (Moving Bistrot) — NON sponsor */
  partner: string;
  /** Sponsor del torneo (Unicar, Diottrica, ecc.) */
  sponsors: string[];
  /** Retrocompat: vecchio campo sponsor singolo */
  sponsor?: string;
  after_party: string;
  description_short: string;
  /** Note aggiuntive libere da riportare sui canali */
  additional_notes: string;
  visual_style?: string;
  visual_mood?: string;
  reference_images: string[];
  start_promo_date?: string | null;
  email_send_date?: string | null;
  whatsapp_start_date?: string | null;
};

export type TournamentDetail = {
  slug: string;
  input: {
    tournament: Partial<TournamentInput>;
    visual?: Record<string, unknown>;
    publishing?: Record<string, unknown>;
  };
  state: Record<string, unknown> | null;
};

// Texts
export type ChannelTexts = {
  instagram_caption: string;
  facebook_post: string;
  whatsapp_message: string;
  email_subject: string;
  email_preheader: string;
  landing_hero_title: string;
  landing_hero_subtitle: string;
  landing_body: string;
  playtomic_titles: Record<string, string>;
  hashtags: string[];
};

// Flyers
export type FlyerEntry = {
  aspect: "1:1" | "4:5" | "9:16";
  filename: string;
  bytes: number;
  modified_at: string;
  url: string;
};

export type FlyersResponse = {
  slug: string;
  flyers: FlyerEntry[];
  flyers_dir: string | null;
};

export type LandingUrlResponse = {
  slug: string;
  deployed: boolean;
  url: string | null;
};

// Clients
export type Client = {
  playtomic_id: string;
  name: string;
  email: string | null;
  phone_number: string | null;
  gender: string;
  padel_level: number | null;
  is_community: boolean;
  wallet_balance: number;
  commercial_communications: boolean;
};

export type ClientsStats = {
  total: number;
  email_contactable: number;
  whatsapp_contactable: number;
  unsubscribed_email: number;
  unsubscribed_whatsapp: number;
  playtomic_opt_in_email: number;
};

export type UnsubscribeEmail = {
  email: string;
  name: string | null;
  note: string | null;
};

export type UnsubscribeWhatsapp = {
  phone_number: string;
  name: string | null;
  note: string | null;
};

export type UnsubscribesBundle = {
  email: UnsubscribeEmail[];
  whatsapp: UnsubscribeWhatsapp[];
};

// Sendings
export type SendingRecord = {
  target: string;
  sent_at: string;
  success: boolean;
  error: string | null;
};

export type SendingSummary = {
  slug: string;
  total: number;
  success: number;
  failed: number;
  last_sent_at: string | null;
  items: SendingRecord[];
};

// Social
export type SocialPost = {
  scheduled_for: string | null;
  published_at: string | null;
  channel: string;
  content_type: string;
  flyer_format: string | null;
  caption: string;
  media_url: string | null;
  platform_post_id: string | null;
  success: boolean;
  error: string | null;
};

export type SocialCalendarDay = {
  date: string;
  days_to_tournament: number;
  actions: string[];
  published: string[];
};

// Pipeline
export type AudienceMode = "all" | "top_fans" | "active_since" | "custom";
export type AudienceGender = "" | "male" | "female";
export type LevelRange = { min: number; max: number };

export type StepRunBody = {
  dry_run?: boolean;
  limit?: number | null;
  feedback?: string;
  regenerate?: boolean;
  landing_url?: string;
  confirm_slug?: string | null;
  // Step 3: se valorizzato, override del prompt NB2 auto-generato
  prompt_override?: string | null;
  // Audience targeting (Fase 8, solo step 6/8)
  audience?: AudienceMode;
  audience_min_tournaments?: number;
  audience_since?: string;
  audience_user_ids?: string[];
  audience_level_ranges?: LevelRange[];
  audience_tournament_name_contains?: string[];
  audience_gender?: AudienceGender;
};

export type AudiencePreview = {
  audience: AudienceMode;
  filter: {
    min_tournaments: number;
    since: string;
    user_ids_count: number;
    level_ranges: LevelRange[];
    tournament_name_contains: string[];
    gender: string;
  };
  selected_clients: number;
  email_contactable: number;
  whatsapp_contactable: number;
  sample: Array<{
    playtomic_id: string;
    name: string;
    email: string;
    phone: string;
    tournaments_count: number;
    last_tournament_date: string;
  }>;
};

export type StepResult = {
  slug: string;
  step: number;
  status: "ok" | "error";
  duration_seconds: number;
  output_summary: Record<string, unknown>;
  error: string | null;
};

export type ApproveResult = {
  slug: string;
  checkpoint: 1 | 2;
  status: string;
  message: string;
};

// ────────────────────────────────────────────────────────────────────────────
// Error
// ────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Core request
// ────────────────────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  init?: RequestInit & { query?: Record<string, string | number | boolean | undefined | null> },
): Promise<T> {
  const { query, ...rest } = init ?? {};

  // Costruisci URL assoluto via window.location.origin per evitare che il
  // browser, se la pagina è stata aperta con credenziali nell URL
  // (es. https://user:pass@host/), le propaghi alla fetch — Chrome 147+
  // throw "URL that includes credentials".
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  let url = path.startsWith("http") ? path : origin + path;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }

  const res = await fetch(url, {
    ...rest,
    // Basic Auth: il browser dialoga con 401 WWW-Authenticate e poi invia
    // le credentials automaticamente. `include` serve per il caso cross-origin
    // (prod: tornei.movingpadel.it); in dev same-origin funziona comunque.
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(rest.headers ?? {}),
    },
  });

  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(res.status, `API ${res.status} on ${path}`, body);
  }

  // 204 no content
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

// ────────────────────────────────────────────────────────────────────────────
// API
// ────────────────────────────────────────────────────────────────────────────

export const api = {
  // System
  health: () => request<HealthCheck>("/api/health"),

  // Tournaments
  listTournaments: (q: {
    status?: TournamentStatus;
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
  } = {}) =>
    request<Page<TournamentSummary>>("/api/tournaments", { query: q }),

  getTournament: (slug: string) =>
    request<TournamentDetail>(`/api/tournaments/${encodeURIComponent(slug)}`),

  createTournament: (payload: unknown) =>
    request<{ slug: string; created: boolean; path: string }>("/api/tournaments", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  patchTournament: (slug: string, payload: unknown) =>
    request<{ slug: string; patched: boolean }>(
      `/api/tournaments/${encodeURIComponent(slug)}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    ),

  deleteTournament: (slug: string) =>
    request<{ slug: string; archived_to: string }>(
      `/api/tournaments/${encodeURIComponent(slug)}`,
      { method: "DELETE" },
    ),

  // Resources
  getTexts: (slug: string) =>
    request<ChannelTexts>(`/api/tournaments/${encodeURIComponent(slug)}/texts`),

  putTexts: (slug: string, texts: ChannelTexts) =>
    request<{ slug: string; updated: boolean; email_invalidated?: boolean }>(
      `/api/tournaments/${encodeURIComponent(slug)}/texts`,
      { method: "PUT", body: JSON.stringify(texts) },
    ),

  getFlyers: (slug: string) =>
    request<FlyersResponse>(`/api/tournaments/${encodeURIComponent(slug)}/flyers`),

  // Flyer admin (prompt + upload manuale) Fase checkpoint
  getFlyerPrompt: (slug: string) =>
    request<{ slug: string; prompt: string; source: string }>(
      `/api/tournaments/${encodeURIComponent(slug)}/flyer-prompt`,
    ),

  saveFlyerPrompt: (slug: string, prompt: string) =>
    request<{ slug: string; saved: boolean; length: number }>(
      `/api/tournaments/${encodeURIComponent(slug)}/flyer-prompt`,
      { method: "PUT", body: JSON.stringify({ prompt }) },
    ),

  uploadManualFlyer: async (slug: string, aspect: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const res = await fetch(
      `${origin}/api/tournaments/${encodeURIComponent(slug)}/flyers/upload?aspect=${encodeURIComponent(aspect)}`,
      { method: "POST", body: fd, credentials: "include" },
    );
    if (!res.ok) {
      let body: unknown;
      try { body = await res.json(); } catch { body = await res.text(); }
      throw new ApiError(res.status, `Upload fallito ${res.status}`, body);
    }
    return (await res.json()) as {
      slug: string;
      aspect: string;
      filename: string;
      url: string;
      bytes: number;
    };
  },

  deleteFlyerAspect: (slug: string, aspect: string) => {
    const safe = aspect.replace(":", "x"); // 1:1 → 1x1
    return request<{ slug: string; aspect: string; deleted: string[] }>(
      `/api/tournaments/${encodeURIComponent(slug)}/flyers/${safe}`,
      { method: "DELETE" },
    );
  },

  getLandingUrl: (slug: string) =>
    request<LandingUrlResponse>(
      `/api/tournaments/${encodeURIComponent(slug)}/landing-url`,
    ),

  // Email preview (step 5 output)
  getEmailPreview: (slug: string) =>
    request<{
      slug: string;
      subject: string;
      preheader: string;
      html: string;
      plain_text: string;
      generated_at: string;
      size_bytes: number;
    }>(`/api/tournaments/${encodeURIComponent(slug)}/email/preview`),

  // Clients
  listClients: (q: {
    page?: number;
    page_size?: number;
    search?: string;
    level_min?: number;
    level_max?: number;
    only_community?: boolean;
  } = {}) => request<Page<Client>>("/api/clients", { query: q }),

  clientsStats: () => request<ClientsStats>("/api/clients/stats"),

  listUnsubscribes: () => request<UnsubscribesBundle>("/api/unsubscribes"),
  listUnsubscribesEmail: () =>
    request<UnsubscribeEmail[]>("/api/unsubscribes/email"),
  listUnsubscribesWhatsapp: () =>
    request<UnsubscribeWhatsapp[]>("/api/unsubscribes/whatsapp"),

  // Playtomic
  discoverPlaytomic: (q: { days_from?: number; days_to?: number } = {}) =>
    request<{
      total: number;
      days_from: number;
      days_to: number;
      items: Array<{
        activity_id: string;
        name: string;
        sport_id: string;
        tournament_date: string;
        time_start: string;
        time_end: string;
        n_slots: number;
        n_resources: number;
        resource_names: string[];
        price_sample: string;
        playtomic_url_hint: string;
        suggested_slug: string;
        already_imported: boolean;
        price_current: string;
        min_players: number;
        max_players: number;
        registered_count: number;
        available_places: number;
        level_description: string;
        gender: string;
        tournament_status: string;
        registration_closing_time: string;
      }>;
    }>("/api/playtomic/tournaments/discover", { query: q }),

  importPlaytomic: (activity_id: string, slug?: string, overwrite = false) =>
    request<{ slug: string; activity_id: string; path: string; created: boolean }>(
      "/api/playtomic/tournaments/import",
      {
        method: "POST",
        body: JSON.stringify({ activity_id, slug, overwrite }),
      },
    ),

  playtomicParticipants: (activity_id: string) =>
    request<{
      activity_id: string;
      tournament_name: string;
      price_current: string;
      min_players: number;
      max_players: number;
      registered_count: number;
      available_places: number;
      tournament_status: string;
      registration_closing_time: string;
      level_description: string;
      gender: string;
      players: Array<{
        user_id: string;
        full_name: string;
        level_value: number | null;
        registration_price: string;
        payment_method_type: string;
        picture: string;
        paid: boolean;
      }>;
    }>(
      `/api/playtomic/tournaments/${encodeURIComponent(activity_id)}/participants`,
    ),

  // ──────────────────────────────────────────────────────────────────────
  // PAST PARTICIPANTS (Fase 7)
  // ──────────────────────────────────────────────────────────────────────
  pastParticipants: (params: {
    min_tournaments?: number;
    since?: string;
    q?: string;
    limit?: number;
  } = {}) =>
    request<{
      generated_at: string;
      days_back: number;
      tournaments_count: number;
      players_count: number;
      cache_age_hours: number;
      filter_min_tournaments: number;
      filter_since: string;
      filter_q: string;
      matched_with_clients: number;
      missing_from_clients: number;
      players: Array<{
        user_id: string;
        full_name: string;
        picture: string;
        level_value: number | null;
        tournaments_count: number;
        last_tournament_date: string;
        tournaments: Array<{
          activity_id: string;
          name: string;
          date: string;
          paid: boolean;
          price: string;
        }>;
        client_matched: boolean;
        email: string;
        phone: string;
        unsubscribed_email: boolean;
        unsubscribed_whatsapp: boolean;
      }>;
    }>("/api/playtomic/past-participants", { query: params }),

  refreshPastParticipants: (days_back = 365) =>
    request<{
      generated_at: string;
      days_back: number;
      tournaments_count: number;
      players_count: number;
    }>("/api/playtomic/past-participants/refresh", {
      method: "POST",
      query: { days_back },
    }),

  // ──────────────────────────────────────────────────────────────────────
  // AUDIENCE PREVIEW (Fase 8)
  // ──────────────────────────────────────────────────────────────────────
  previewAudience: (params: {
    audience: AudienceMode;
    min_tournaments?: number;
    since?: string;
    user_ids?: string[];
    level_ranges?: LevelRange[];
    tournament_name_contains?: string[];
    gender?: AudienceGender;
  }) =>
    request<AudiencePreview>("/api/audience/preview", {
      method: "POST",
      body: JSON.stringify({
        audience: params.audience,
        min_tournaments: params.min_tournaments ?? 2,
        since: params.since ?? "",
        user_ids: params.user_ids ?? [],
        level_ranges: params.level_ranges ?? [],
        tournament_name_contains: params.tournament_name_contains ?? [],
        gender: params.gender ?? "",
      }),
    }),

  importClientsCsv: async (
    file: File,
    mode: "replace" | "merge" = "replace",
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const res = await fetch(`${origin}/api/clients/import`, {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text();
      }
      throw new ApiError(res.status, `Import failed ${res.status}`, body);
    }
    return (await res.json()) as {
      mode: string;
      rows_imported: number;
      rows_before: number;
      rows_after: number;
      backup_path: string;
      warnings: string[];
    };
  },

  // Monitoring
  emailSendings: (slug: string, limit = 500) =>
    request<SendingSummary>(
      `/api/tournaments/${encodeURIComponent(slug)}/sendings/email`,
      { query: { limit } },
    ),

  whatsappSendings: (slug: string, limit = 500) =>
    request<SendingSummary>(
      `/api/tournaments/${encodeURIComponent(slug)}/sendings/whatsapp`,
      { query: { limit } },
    ),

  socialPosts: (slug: string, limit = 200) =>
    request<SocialPost[]>(
      `/api/tournaments/${encodeURIComponent(slug)}/social-posts`,
      { query: { limit } },
    ),

  socialCalendar: (slug: string) =>
    request<SocialCalendarDay[]>(
      `/api/tournaments/${encodeURIComponent(slug)}/social-calendar`,
    ),

  // Pipeline
  runStep: (slug: string, n: number, body: StepRunBody = {}) =>
    request<StepResult>(
      `/api/tournaments/${encodeURIComponent(slug)}/steps/${n}`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  approveTexts: (slug: string) =>
    request<ApproveResult>(
      `/api/tournaments/${encodeURIComponent(slug)}/approve-texts`,
      { method: "POST", body: "{}" },
    ),

  approveFlyer: (slug: string) =>
    request<ApproveResult>(
      `/api/tournaments/${encodeURIComponent(slug)}/approve-flyer`,
      { method: "POST", body: "{}" },
    ),

  regenerateFlyer: (slug: string, feedback: string = "") =>
    request<StepResult>(
      `/api/tournaments/${encodeURIComponent(slug)}/regenerate-flyer`,
      { method: "POST", body: JSON.stringify({ feedback }) },
    ),

  // Bitly
  shortenBitly: (long_url: string, keyword_hint?: string) =>
    request<{
      link: string;
      bitlink_id: string;
      long_url: string;
      custom: boolean;
      created_at: string;
    }>("/api/bitly/shorten", {
      method: "POST",
      body: JSON.stringify({ long_url, keyword_hint }),
    }),

  // Reference images for NB2
  listReferenceImages: (slug: string) =>
    request<{
      slug: string;
      images: Array<{
        filename: string;
        url: string;
        bytes: number;
        modified_at: string;
      }>;
    }>(`/api/tournaments/${encodeURIComponent(slug)}/reference-images`),

  uploadReferenceImages: async (slug: string, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const res = await fetch(
      `${origin}/api/tournaments/${encodeURIComponent(slug)}/reference-images`,
      { method: "POST", body: fd, credentials: "include" },
    );
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text();
      }
      throw new ApiError(res.status, `Upload failed ${res.status}`, body);
    }
    return (await res.json()) as {
      slug: string;
      uploaded: string[];
      images: Array<{
        filename: string;
        url: string;
        bytes: number;
      }>;
    };
  },

  deleteReferenceImage: (slug: string, filename: string) =>
    request<{ slug: string; deleted: string }>(
      `/api/tournaments/${encodeURIComponent(slug)}/reference-images/${encodeURIComponent(filename)}`,
      { method: "DELETE" },
    ),
  // ──────────────────────────────────────────────────────────────────────
  // MESSAGING HUB (Control Center)
  // ──────────────────────────────────────────────────────────────────────
  messagingHealth: () => request<MessagingHealth>("/api/messaging/health"),

  messagingGroups: () => request<MessagingGroups>("/api/messaging/groups"),

  messagingPreview: (spec: MessagingSpec) =>
    request<MessagingPreviewResponse>("/api/messaging/preview", {
      method: "POST",
      body: JSON.stringify(spec),
    }),

  messagingSend: (spec: MessagingSpec) =>
    request<MessagingSendResponse>("/api/messaging/send", {
      method: "POST",
      body: JSON.stringify(spec),
    }),

  messagingJobs: () =>
    request<{ jobs: MessagingJobSummary[] }>("/api/messaging/jobs"),

  messagingJob: (jobId: string) =>
    request<MessagingJob>(`/api/messaging/jobs/${encodeURIComponent(jobId)}`),

  messagingAudit: (params: { channel?: string; label?: string; limit?: number } = {}) =>
    request<MessagingAuditResponse>("/api/messaging/audit", { query: params }),

  // ──────────────────────────────────────────────────────────────────────
  // AUTOMAZIONI (Control Center — Dashboard sistemi)
  // ──────────────────────────────────────────────────────────────────────
  listAutomations: () => request<AutomationsResponse>("/api/automations"),

  automationLogs: (unit: string, lines = 80) =>
    request<{ unit: string; lines: string[] }>(
      `/api/automations/${encodeURIComponent(unit)}/logs`,
      { query: { lines } },
    ),

  // ──────────────────────────────────────────────────────────────────────
  // COMMUNITY & VANTAGGI (Control Center)
  // ──────────────────────────────────────────────────────────────────────
  communityOverview: () => request<CommunityOverview>("/api/community/overview"),

  pickleballOverview: () => request<PickleballOverview>("/api/pickleball/overview"),

};

// ────────────────────────────────────────────────────────────────────────────
// WebSocket helper
// ────────────────────────────────────────────────────────────────────────────
// Messaging Hub types (Control Center)
// ────────────────────────────────────────────────────────────────────────────
export type MessagingChannel =
  | "playtomic_dm"
  | "playtomic_broadcast"
  | "whatsapp_group"
  | "email";

export type MessagingAudienceSpec = {
  audience: AudienceMode;
  min_tournaments?: number;
  since?: string;
  user_ids?: string[];
  level_ranges?: LevelRange[];
  tournament_name_contains?: string[];
  gender?: AudienceGender;
};

export type MessagingSpec = {
  channel: MessagingChannel;
  label?: string;
  text: string;
  subject?: string;
  link?: string;
  audience?: MessagingAudienceSpec;
  targets?: string[];
  dry_run?: boolean;
  confirm?: boolean;
  jitter_min_sec?: number;
  jitter_max_sec?: number;
  limit?: number | null;
  skip_already_sent?: boolean;
};

export type MessagingOptoutDetail = {
  blocked: number;
  by_uid: number;
  by_email: number;
  by_phone: number;
};

export type MessagingPreviewStats = {
  channel: string;
  raw: number;
  blocked_optout: number;
  optout_detail: MessagingOptoutDetail;
  already_sent: number;
  net_recipients: number;
};

export type MessagingSample = { target: string; name: string; message: string };

export type MessagingPreviewResponse = {
  channel: string;
  label: string;
  stats: MessagingPreviewStats;
  sample: MessagingSample[];
  estimated_duration_sec: number;
  optout_sources: { emails: number; phones: number; uids: number };
};

export type MessagingSendResponse = {
  dry_run: boolean;
  job_id?: string;
  status?: string;
  launcher?: string;
  log_slug?: string;
  stats?: MessagingPreviewStats;
  sample?: MessagingSample[];
  estimated_duration_sec?: number;
};

export type MessagingJobSummary = {
  job_id: string;
  channel: string;
  label: string;
  status: string;
  total: number | null;
  sent: number | null;
  failed: number | null;
  created_at: string;
  finished_at?: string;
};

export type MessagingJob = MessagingJobSummary & {
  text?: string;
  link?: string;
  stats?: MessagingPreviewStats;
  last_target?: string;
  started_at?: string;
  error?: string;
};

export type WAGroup = {
  name: string;
  jid: string;
  enabled: boolean;
  type: string;
  level_min?: number | null;
  level_max?: number | null;
  gender?: string | null;
};

export type PlaytomicCommunity = { chat_id: string; name: string };

export type MessagingGroups = {
  whatsapp_groups: WAGroup[];
  playtomic_communities: PlaytomicCommunity[];
};

export type MessagingAuditSummaryRow = {
  label: string;
  channel: string;
  ok: number;
  failed: number;
  last_ts: string;
};

export type MessagingAuditResponse = {
  summary: MessagingAuditSummaryRow[];
  detail: {
    total: number;
    filtered_total: number;
    ok: number;
    failed: number;
    items: Array<Record<string, unknown>>;
  };
};

export type MessagingHealth = {
  optout: { emails: number; phones: number; uids: number };
  channels: string[];
  playtomic: { ok: boolean; uid?: string; error?: string };
};

// ────────────────────────────────────────────────────────────────────────────
// Automations types (Control Center)
// ────────────────────────────────────────────────────────────────────────────
export type AutomationHealth = "ok" | "error" | "off" | "idle" | "unknown";

export type AutomationUnit = {
  unit: string;
  kind: string;
  label: string;
  desc: string;
  active_state: string;
  sub_state: string;
  enabled: string;
  health: AutomationHealth;
  next_run: string | null;
  last_run: string | null;
};

export type AutomationSystem = {
  key: string;
  label: string;
  icon: string;
  external_url?: string | null;
  units: AutomationUnit[];
};

export type AutomationsResponse = {
  systems: AutomationSystem[];
  summary: { total: number; ok: number; error: number; off: number };
};

// ────────────────────────────────────────────────────────────────────────────
// Community types (Control Center)
// ────────────────────────────────────────────────────────────────────────────
export type CommunityInfo = {
  name: string;
  group_id?: string;
  status?: string;
  members: number | null;
  target: number;
  gap: number | null;
  pct: number | null;
};

export type CommunityOverview = {
  communities: CommunityInfo[];
  filler: {
    date: string | null;
    follows_today: number;
    adds_today: number;
    follow_limit: number;
    add_limit: number;
  };
  blocklist_count: number;
  welcome: { welcomed: number; first_run_done: boolean };
  vouchers: {
    available: number;
    dispensed: number;
    campaigns: Array<{ campaign_id: string; available: number }>;
  };
  benefits: Array<{ category: string; members: number }>;
};

// ────────────────────────────────────────────────────────────────────────────
// Pickleball monitor types (Control Center)
// ────────────────────────────────────────────────────────────────────────────
export type PickleballOverview = {
  users_total: number;
  bookings_by_status: Record<string, number>;
  today_bookings: number;
  upcoming_bookings: number;
  revenue_confirmed_eur: number;
  next_matches: Array<{
    date: string;
    start: string;
    kind: string;
    status: string;
    players: number;
    max_players: number;
  }>;
  app_url: string;
};

// ────────────────────────────────────────────────────────────────────────────

export type LogMessage =
  | { type: "meta"; slug: string; path: string; exists: boolean; connected_at: string }
  | { type: "line"; text: string }
  | { type: "error"; message: string };

export function openLogStream(
  slug: string,
  onMessage: (msg: LogMessage) => void,
  onClose?: (ev: CloseEvent) => void,
): WebSocket {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/ws/logs/${encodeURIComponent(slug)}`);
  ws.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data) as LogMessage;
      onMessage(data);
    } catch {
      // ignore malformed
    }
  };
  if (onClose) ws.onclose = onClose;
  return ws;
}
